// @flow
import * as ReactIs from 'react-is';

type ValidationFailure = {|
  // A description for the checker that produced the failure.
  // TODO typeDescription is probably not quite right, since
  // we want to extend this checker system to go beyond traditional
  // "types"
  typeDescription: string,
  // Content-author-readable reason that validation failed.
  reason: string,
  // For "composite" checkers, there can be many multiple underlying failures that
  // lead to the check failing. For example, the arrayOf checker might find multiple
  // elements which fail. By adding this extra metadata to the validation failure
  // higher layers can get access to the underlying reasons. The intent is to aid
  // in the compositionality of checkers.
  //
  // TODO determine how useful this is in practice for composition of checkers.
  failures?: ValidationFailure[],
|};

// NOTE: this is how to type a function with a static description property on it.
export type Checker = {
  (props: { [k: string]: any }, propName: string): ValidationFailure | null,
  description: string,
};

type FailureMessage = {
  message: string,
  ...Location,
};

function createPrimitiveTypeChecker(primitiveType): Checker {
  function checker(props, propName) {
    const value = props[propName];
    if (typeOf(value) !== primitiveType) {
      return {
        typeDescription: primitiveType,
        reason: `must be a ${primitiveType} not a ${typeOf(value)}`,
      };
    }
    return null;
  }
  checker.description = primitiveType;
  return checker;
}

function anyChecker(_props, _propName) {
  return null;
}
anyChecker.description = `any`;

function createArrayOfChecker(itemChecker: Checker): Checker {
  function arrayOfChecker(props, propName) {
    const value = props[propName];
    if (!Array.isArray(value)) {
      const { description } = arrayOfChecker;
      return {
        typeDescription: description,
        reason: `must be an ${description}, not a ${typeOf(value)}`,
      };
    }

    const failures = [];
    value.forEach((item, i) => {
      // TODO
      // This is telling us how these compose a bit weirdly. Technically this works
      // since indexing into object and arrays works in the same way. Note sure
      // the nicest way to resolve this TBH... I don't want to pollute the Checker
      // interface with | Array.
      const failure = itemChecker(value, i);
      if (failure !== null) failures.push(failure);
    });

    if (failures.length > 0) {
      const { description } = arrayOfChecker;
      return {
        typeDescription: description,
        // TODO should probably describe what the various failures were...
        reason: `must be an ${description}. ${failures.length} items are the wrong type.`,
        failures,
      };
    }

    return null;
  }
  arrayOfChecker.description = `Array<${itemChecker.description}>`;

  return arrayOfChecker;
}

// TODO not sure if we can model this meaning of Function properly in Flow since
// Function is now just an alias for any.
function createInstanceOfChecker(expectedClass: Function): Checker {
  const className = expectedClass.name || `\`😭NoClassNameFound😭\``;

  function instanceOfChecker(props, propName) {
    const value = props[propName];
    if (!(value instanceof expectedClass)) {
      const { description } = instanceOfChecker;
      return {
        typeDescription: description,
        reason: `must be an instance of ${className}`,
      };
    }

    return null;
  }
  instanceOfChecker.description = `instance of ${className}`;

  return instanceOfChecker;
}

function createObjectOfChecker(propertyChecker: Checker): Checker {
  function objectOfChecker(props, propName) {
    const value = props[propName];

    if (typeOf(value) !== 'object') {
      const { description } = objectOfChecker;
      return {
        typeDescription: description,
        reason: `must be an ${description}, not a ${typeOf(value)}`,
      };
    }

    const failedKeys = [];
    for (const [k] of Object.entries(value)) {
      const failure = propertyChecker(value, k);
      if (failure !== null) {
        failedKeys.push(k);
      }
    }

    if (failedKeys.length > 0) {
      const { description } = objectOfChecker;
      return {
        typeDescription: description,
        reason: `must be an ${description}. The properties ${failedKeys
          .map((k) => `\`${k}\``)
          .join(', ')} are the wrong type.`,
      };
    }

    return null;
  }
  objectOfChecker.description = `Object wherein each value is a ${propertyChecker.description}`;

  return objectOfChecker;
}

function createUnionOfValuesChecker(...union: mixed[]): Checker {
  function unionOfValuesChecker(props, propName) {
    const value = props[propName];

    if (union.some((unionValue) => Object.is(value, unionValue))) {
      return null;
    }

    const { description } = unionOfValuesChecker;
    return {
      typeDescription: description,
      reason: `must be ${description}, not ${String(value)}.`,
    };
  }
  unionOfValuesChecker.description = `${union.join(' | ')}`;

  return unionOfValuesChecker;
}

function createUnionOfTypesChecker(...union: Checker[]): Checker {
  function unionOfTypesChecker(props, propName) {
    const failures = [];
    for (const checker of union) {
      const failure = checker(props, propName);
      // If one checker passes, the union type is satisfied.
      if (failure === null) return null;
      failures.push(failure);
    }

    return {
      // Producing the checker is so that the level
      // above can access the description of the checker.
      typeDescription: unionOfTypesChecker.description,
      // Reason is used in every case except the
      // root case where the reason gets manually
      // constructed for increased clarity.
      reason: `needs to be one of: ${failures
        .map((f) => f.typeDescription)
        .join(' | ')}`,
      // Failures is only consumed at the root
      // to give a better description for composite
      // checkers.
      failures,
    };
  }
  // We place this on the checker so that we can
  // dynamically create descriptions based upon
  // particular checker lists given to us at runtime.
  unionOfTypesChecker.description = `${union
    .map((ch) => ch.description)
    .join(' | ')}`;

  return unionOfTypesChecker;
}

function createShapeChecker(shape: { [propName: string]: Checker }): Checker {
  function shapeChecker(props, propName) {
    const value = props[propName];

    if (!isObject(value)) {
      const { description } = shapeChecker;
      return {
        typeDescription: description,
        reason: `must be an ${description}, not a ${typeOf(value)}`,
      };
    }

    const missingKeys = [];
    const failedKeys = [];
    for (const [key, checker] of Object.entries(shape)) {
      if (!value.hasOwnProperty(key)) {
        missingKeys.push(key);
        continue;
      }

      const failure = checker(value, key);
      if (failure !== null) failedKeys.push(key);
    }

    if (missingKeys.length > 0 || failedKeys.length > 0) {
      const missingMsg =
        missingKeys.length > 0
          ? `${missingKeys.map((k) => `\`${k}\``).join(', ')} are missing. `
          : '';
      const failedMsg =
        failedKeys.length > 0
          ? `${failedKeys
              .map((k) => `\`${k}\``)
              .join(', ')} are the wrong type.`
          : '';
      const { description } = shapeChecker;
      return {
        typeDescription: description,
        reason: `must be a ${description}. ${missingMsg}${failedMsg}`,
        // TODO should probably do a better job with the missing/failed
        // and then capture the optional failures property here.
      };
    }

    return null;
  }

  let shapeStr = '{ ';
  shapeStr += Object.entries(shape)
    .map(([k, v]) => `${k}: ${v.description}`)
    .join(', ');
  shapeStr += ' }';
  shapeChecker.description = `Object with this shape: ${shapeStr}`;

  return shapeChecker;
}

// TODO try to reimplement this so that it can compose createShapeChecker
// as this is a litmus test for compositionality of these checkers to
// some extent.
function createExactShapeChecker(shape: {
  [propName: string]: Checker,
}): Checker {
  function exactShapeChecker(props, propName) {
    const value = props[propName];

    if (typeOf(value) !== 'object') {
      const { description } = exactShapeChecker;
      return {
        typeDescription: description,
        reason: `must be an ${description}, not a ${typeOf(value)}`,
      };
    }

    const missingKeys = [];
    const failedKeys = [];
    for (const [key, checker] of Object.entries(shape)) {
      if (!value.hasOwnProperty(key) && !isOptionalSpec(checker)) {
        missingKeys.push(key);
        continue;
      }

      const failure = checker(value, key);
      if (failure !== null) failedKeys.push(key);
    }

    const excessKeys = [];
    for (const key of Object.keys(value)) {
      if (!shape.hasOwnProperty(key)) excessKeys.push(key);
    }

    if (
      missingKeys.length > 0 ||
      failedKeys.length > 0 ||
      excessKeys.length > 0
    ) {
      const missingMsg =
        missingKeys.length > 0
          ? `${missingKeys.map((k) => `\`${k}\``).join(', ')} are missing.`
          : '';
      const failedMsg =
        failedKeys.length > 0
          ? `${failedKeys
              .map((k) => `\`${k}\``)
              .join(', ')} are the wrong type.`
          : '';
      const excessMsg =
        excessKeys.length > 0
          ? `${excessKeys.map((k) => `\`${k}\``).join(', ')} are not allowed.`
          : '';
      const keyMsgs = [missingMsg, failedMsg, excessMsg]
        .filter((msg) => msg !== '')
        .join(' ');
      const { description } = exactShapeChecker;
      return {
        typeDescription: description,
        reason: `must be a ${description}. ${keyMsgs}`,
        // TODO should probably do a better job with the missing/failed/excess
        // and then capture the optional failures property here.
      };
    }

    return null;
  }

  let shapeStr = '{ ';
  shapeStr += Object.entries(shape)
    .map(([k, v]) => `${k}: ${v.description}`)
    .join(', ');
  shapeStr += ' }';
  exactShapeChecker.description = `Object with this exact shape: ${shapeStr}`;

  return exactShapeChecker;
}

function createTupleChecker(tuple: Array<Checker>): Checker {
  function tupleChecker(props, propName) {
    const value = props[propName];

    if (!Array.isArray(value)) {
      const { description } = tupleChecker;
      return {
        typeDescription: description,
        reason: `must be a ${description}, not a ${typeOf(value)}`,
      };
    }

    if (value.length !== tuple.length) {
      const { description } = tupleChecker;
      return {
        typeDescription: description,
        reason: `must be a ${description}, not a ${value.length}-tuple`,
      };
    }

    const failures = [];
    tuple.forEach((checker, i) => {
      const failure = checker(value, i);
      if (failure !== null) {
        failures.push(failure);
      }
    });

    if (failures.length > 0) {
      const { description } = tupleChecker;
      return {
        typeDescription: description,
        reason: `must be a ${description}, ${failures.length} items have the wrong type`,
        failures,
      };
    }

    return null;
  }
  tupleChecker.description = `${tuple.length}-tuple: [${tuple
    .map((c) => c.description)
    .join()}]`;
  return tupleChecker;
}

// A checker that makes sure each checker passed into it passes at least once.
function createSupersetChecker(...checkers: Array<Checker>) {
  function supersetChecker(props, propName) {
    let values = props[propName];
    if (!Array.isArray(values)) values = [values];
    const checkerSet = checkers.map((_, index) => [index, false]);
    for (let valueIndex = 0; valueIndex < values.length; valueIndex++) {
      // iterate over all checkers
      let passed = false;
      for (
        let checkerIndex = 0;
        checkerIndex < checkers.length;
        checkerIndex++
      ) {
        const result = checkers[checkerIndex](values, valueIndex);
        if (result == null) {
          checkerSet[checkerIndex][1] = true;
          passed = true;
          break;
        }
      }
      if (passed === false) {
        return {
          typeDescription: supersetChecker.description,
          reason: `must only contain items from the set: ${checkers
            .map((c) => c.description)
            .join(',')}`,
        };
      }
    }
    const remainingCheckers = checkerSet.filter(([_index, value]) => !value);
    if (remainingCheckers.length !== 0) {
      return {
        typeDescription: supersetChecker.description,
        reason: `must contain all or more of the following ${checkers
          .map((c) => c.description)
          .join(',')}`,
      };
    }
    return null;
  }
  supersetChecker.description = `superset of ${checkers
    .map((c) => c.description)
    .join(',')}`;
  return supersetChecker;
}

// This is disctinct from the TupleChecker to provide a better error message to content authors
function createExactOrderChildrenArrayChecker(...checkers: Array<Checker>) {
  const reason = `must be in the exact order ${checkers
    .map(({ description }) => description)
    .join(', ')}`;

  function exactOrderChildrenArrayChecker(props, propName) {
    let children = props[propName];
    if (!Array.isArray(children)) children = [children];

    // This is used to correct for optional checkers that creates as situation where there are less
    // children than there are checkers

    const countNonOptionalCheckers = checkers.filter(
      (checker) => !checker.optionalChecker,
    ).length;

    let checkerOffset = 0;
    let childIndex;
    for (childIndex = 0; childIndex < children.length; childIndex++) {
      const checker = checkers[childIndex + checkerOffset];
      // situation is valid if the checkeroffset + childIndex is greater than the supplied checkers
      // And there is no valid optional checker that can satisfy the given child
      // Or if there is a child present but no more valid checkers to check that child.
      if (checker == null) {
        return {
          typeDescription: exactOrderChildrenArrayChecker.description,
          reason,
        };
      }
      // Checkers receive a dictionary and a key to access a prop name.
      // this is a kludge but operates the same way
      const result = checker(children, childIndex);

      // There is a valid configuration where there are more checkers than children because of
      // optional children.
      if (result != null && checker.optionalChecker) {
        // Stay on the current child and increment the checker offset
        childIndex--;
        checkerOffset++;
        continue;
      }

      if (result != null)
        return {
          typeDescription: exactOrderChildrenArrayChecker.description,
          reason: `${reason}. Element #${childIndex + checkerOffset} ${
            result.reason
          }`,
        };
    }

    if (
      childIndex + checkerOffset !== checkers.length &&
      countNonOptionalCheckers > children.length
    ) {
      return {
        typeDescription: exactOrderChildrenArrayChecker.description,
        reason: `${reason}. You may have too few items specified`,
      };
    }

    return null;
  }

  exactOrderChildrenArrayChecker.description = `ExactOrderChildrenArray<${checkers
    .map(({ description }) => description)
    .join(', ')}>`;
  return exactOrderChildrenArrayChecker;
}

// Creates a checker that validates an array of children with the special condition
// that the last checker in the array is given the remaining unchecked children for validation
function createPrefixChildrenArrayChecker(...checkers: Array<Checker>) {
  const [first, last] = [
    checkers.slice(0, checkers.length - 1),
    checkers.slice(checkers.length - 1),
  ];

  function prefixChildrenArrayChecker(props, propName) {
    let children = props[propName];
    if (!Array.isArray(children)) children = [children];
    const prefixes = first;

    for (const prefixIndex in prefixes) {
      const checker = checkers[prefixIndex];
      const valid = checker(children, prefixIndex);
      if (valid != null) {
        return {
          typeDescription: prefixChildrenArrayChecker.description,
          reason: `must begin with ${prefixes
            .map(({ description }) => description)
            .join(', ')}. Element ${prefixIndex} ${valid.reason}`,
        };
      }
    }

    const finalChecker = last[0];

    const valid = finalChecker(
      { [propName]: children.slice(checkers.length - 1) },
      propName,
    );
    if (valid != null) {
      return {
        typeDescription: prefixChildrenArrayChecker.description,
        reason: `final element ${valid.reason}`,
      };
    }
    return null;
  }

  prefixChildrenArrayChecker.description = `a set of children that are prefixed with ${first
    .map(({ description }) => description)
    .join(', ')} and ending with ${last
    .map(({ description }) => description)
    .join(', ')}`;

  return prefixChildrenArrayChecker;
}

// This function does the coercion from single items to array for items that require mappings
// over children
function createChildrenChecker(checker: Checker) {
  function children(props, propName) {
    let value = props[propName];
    if (!Array.isArray(value)) value = [value];
    return checker({ [propName]: value }, propName);
  }
  children.description = checker.description;
  return children;
}
// Validates that a checker is applicable for N or more values of an array passed to it
function createMinimumCountChecker(count: number, checker: Checker) {
  function minimumCountChecker(props, propName) {
    let value = props[propName];
    if (!Array.isArray(value)) value = [value];
    if (count > value.length) {
      return {
        typeDescription: minimumCountChecker.description,
        reason: `must be a list of ${checker.description} with ${count} or more items`,
      };
    }

    for (const childIndex in value) {
      const valid = checker(value, childIndex);
      if (valid != null) {
        return {
          typeDescription: minimumCountChecker.description,
          reason: `must be a list of ${checker.description} with ${count} or more items. Element ${childIndex} ${valid.reason}`,
        };
      }
    }

    return null;
  }

  minimumCountChecker.description = `a list of ${checker.description} with ${count} or more items`;
  return minimumCountChecker;
}

function nodeChecker(props, propName) {
  const value = props[propName];
  if (!isNode(value)) {
    const { description } = nodeChecker;
    return {
      typeDescription: description,
      reason: `must be a valid ${description}`,
    };
  }

  return null;
}
nodeChecker.description = `ReactNode`;

function elementChecker(props, propName) {
  const value = props[propName];
  if (!ReactIs.isElement(value)) {
    const { description } = elementChecker;
    return {
      typeDescription: description,
      reason: `must be an ${description} not a ${typeOf(value)}`,
    };
  }

  return null;
}
// TODO not sure if this is the best terminology for content authors.
elementChecker.description = `Element`;

function componentChecker(props, propName) {
  const value = props[propName];
  if (!ReactIs.isValidElementType(value)) {
    const { description } = componentChecker;
    return {
      typeDescription: description,
      reason: `must be a ${description} not a ${typeOf(value)}`,
    };
  }

  return null;
}
componentChecker.description = `Component`;

// TODO not sure if we can model elementType properly in Flow.
// It's either a Function or an instanceof React.Component.
// Actually the react typedefs probably have something suitable no?
function createElementTypeChecker(elementType: Function): Checker {
  const expectedName = getComponentName(elementType);

  function elementTypeChecker(props, propName) {
    const value = props[propName];
    if (!ReactIs.isElement(value)) {
      const { description } = elementTypeChecker;
      return {
        typeDescription: description,
        reason: `must be a ${description} not a ${typeOf(value)}`,
      };
    }

    const actualName = getComponentName(value.type);
    // This is a naive form of nominal type checking that works
    // OK for our purposes due to the restricted way in which
    // the content editor currently works. If the editor becomes
    // more general purpose this will start breaking down.
    if (actualName !== expectedName) {
      const { description } = elementTypeChecker;
      return {
        typeDescription: description,
        reason: `must be a ${description} not <${actualName}>`,
      };
    }

    return null;
  }
  elementTypeChecker.description = `<${expectedName}>`;
  return elementTypeChecker;
}

function createChildrenArrayChecker(childChecker: Checker): Checker {
  function childArrayChecker(props, propName) {
    let children = props[propName];
    if (!Array.isArray(children)) children = [children];

    const failures = [];
    for (let i = 0; i < children.length; i++) {
      const failure = childChecker(children, i);
      if (failure !== null) failures.push(failure);
    }

    if (failures.length > 0) {
      const { description } = childArrayChecker;
      return {
        typeDescription: description,
        reason:
          failures.length === 1
            ? `must only contain \`${childChecker.description}\`s. The child is not a \`${childChecker.description}\``
            : `must only contain \`${childChecker.description}\`s. ${failures.length} children are not \`${childChecker.description}\`s`,
      };
    }

    return null;
  }
  childArrayChecker.description = `ChildrenArray<${childChecker.description}>`;

  return childArrayChecker;
}

function createOptionalChecker(checker: Checker): Checker {
  function optionalChecker(props, propName) {
    // Optional checkers are satisfied if the prop is not passed at all.
    if (props[propName] === undefined) return null;
    return checker(props, propName);
  }
  optionalChecker.description = `optional(${checker.description})`;
  optionalChecker.optionalChecker = true; // TODO: Maybe use a symbol here
  return optionalChecker;
}

function latexChecker(props, propName) {
  const value = props[propName];

  const failure = createPrimitiveTypeChecker('string')(props, propName);
  if (failure !== null) return failure;

  const disallowedStrings = validateLatex(value);

  if (disallowedStrings.length > 0) {
    return {
      typeDescription: latexChecker.description,
      reason: `must not contain: \n  ${disallowedStrings.join(
        '\n  ',
      )} \nCheck our LaTeX style guide for what is allowed.`,
    };
  }

  // Special case the check for latex comments so we can give a better description.
  // This regex matches any % sign that is NOT preceded by a \
  // "% comment %"  // Bad
  // "27\%"         // Good (renders 27%)
  const re = new RegExp(String.raw`(?<!\\)%`);
  if (value.match(re)) {
    return {
      typeDescription: latexChecker.description,
      reason: `must not contain Latex comments. If you were trying to display the percent sign, you need to use \\% instead.`,
    };
  }

  return null;
}
latexChecker.description = 'LatexExpression';

const uuidRegex =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function uuidChecker(props, propName) {
  const value = props[propName];

  const failure = createPrimitiveTypeChecker('string')(props, propName);
  if (failure !== null) return failure;
  if (typeof value !== 'string') {
    throw Error('runtime typecheck invariant failure');
  }

  if (!uuidRegex.test(value)) {
    return {
      typeDescription: uuidChecker.description,
      reason:
        'must be a valid UUID. Use the autocomplete to get a valid id generated for you.',
    };
  }

  return null;
}
uuidChecker.description = 'UUID';

// validateLatex is designed to check a *valid* latex string, and then
// constrain it to a subset of allowed commands. It is not designed to
// check the latex grammar itself. The reason we constrain the commands
// is because:
// a) there are many ways to express the same thing in latex, and we
//    only want to allow one way (as far as that's possible).
// b) latex contains lots of "easily abused" commands. Changing text color
//    font face, display mode, etc. Essentially a lot of things that have
//    nothing to do with expressing mathematical concepts. Content authors
//    could and would abuse this stuff to degrade the quality of our content if
//    this were not prevented.
function validateLatex(latex) {
  const cmds = getCommands(latex);
  const disallowedStrings = [];
  for (const cmd of cmds) {
    if (!whitelistedCommands.has(cmd)) {
      disallowedStrings.push(`\\${cmd}`);
    }
  }

  // 🚨 This is the first command where we need to constrain the arguments.
  // Since its currently an edge case we'll do it in this clumsy way.
  // If another command pops up that needs to constrain arguments, make
  // a generalizable solution to this.
  if (cmds.includes('mathbb')) {
    const matches = latex.matchAll(/\\mathbb{([^}]*)}/g);
    const args = new Set(Array.from(matches, (match) => match[1]));
    for (const arg of args) {
      if (arg !== 'Q') disallowedStrings.push(`\\mathbb{${arg}}`);
    }
  }

  if (latex.includes('~')) {
    disallowedStrings.push('~');
  }

  return disallowedStrings;
}

function getCommands(latex) {
  const namedCmds = String.raw`[a-zA-Z@]+`;
  const symbolCmds = String.raw`[^\uD800-\uDFFF]`;
  const cmdRegexString = String.raw`\\(${namedCmds}|${symbolCmds})`;
  const re = new RegExp(cmdRegexString, 'g');
  return [...latex.matchAll(re)].map((match) => match[1]);
}

const whitelistedCommands = new Set([
  // Mode switcher
  'text',

  // Units
  // In diagnostic questions \operatorname{cm} is used for units as MathLive
  // uses this format and we want you to be able to copy-paste Katex-rendered
  // latex into the MathLive input. If we used \text{cm} in Katex, then copy
  // pasting would cause students to be marked incorrect as the correct
  // answer will be specified as what MathLive naturally produces if you type
  // in the answer explicitly. In this example, that would be \operatorname{cm}
  'operatorname',

  // Binary operator
  'times',
  'cdot',
  'div',
  'pm',
  'mp',

  // This is a non-standard operator symbol used to represent abitrary operations
  // between categories. There are some standard uses but, outside of higher level
  // math, it is mainly used in a similar way to pronumerals but to instead
  // represents operations rather than objects.
  'star',

  // Functions
  'log',
  'ln',
  'sqrt',

  // Relational operators
  'gt',
  'lt',
  'geq',
  'leq',
  'neq',
  'approx',
  'doteqdot',
  'propto',
  'ngtr',
  'nless',
  'ngeq',
  'nleq',

  // Set theory
  'cup',
  'cap',
  'setminus',
  'in',
  'notin',
  'subset',
  'supset',
  'subseteq',
  'supseteq',
  'nsubseteq',
  'nsupseteq',
  'lor',
  'land',
  'neg',
  'N',
  'Z',
  // Annoyingly Katex doesn't support \Q, all the other common sets seem to be
  // supported: https://katex.org/docs/support_table#qr
  // So we'll "globally" enable \mathbb{} commands, but then constrain it to only
  // allow Q as an arg over in the validateLatex function (for now anyways).
  'mathbb',
  'Reals',
  'Complex',
  'emptyset',
  'aleph',
  'middle',
  'forall',
  'exists',
  'implies',
  'impliedby',
  'iff',
  'therefore',
  'because',

  'frac',
  'dfrac', // debatable

  // Greek letters
  'alpha',
  'beta',
  'gamma',
  'delta',
  'epsilon',
  'zeta',
  'etaΗ',
  'theta',
  'iota',
  'kappa',
  'lambda',
  'mu',
  'nu',
  'xi',
  'omicron',
  'pi',
  'rho',
  'sigma',
  'tau',
  'upsilon',
  'phi',
  'chi',
  'psi',
  'omega',
  'Alpha',
  'Beta',
  'Gamma',
  'Delta',
  'Epsilon',
  'Zeta',
  'EtaΗ',
  'Theta',
  'Iota',
  'Kappa',
  'Lambda',
  'Mu',
  'Nu',
  'Xi',
  'Omicron',
  'Pi',
  'Rho',
  'Sigma',
  'Tau',
  'Upsilon',
  'Phi',
  'Chi',
  'Psi',
  'Omega',

  // Math symbol
  'infty',
  'degree',
  '%',
  '{',
  '}',
  '|', // produces double pipe

  // Currency
  '$',
  'yen',
  'pounds',

  // Random glyphs
  'checkmark', // Do we really want this?

  // Parentheses
  'left',
  'middle',
  'right',

  // Spacing
  ' ', // TODO we should settle on either this or \,
  ',',
  'enspace',
  'quad',
  'qquad',
  '\\', // newline

  // Dots
  'ldots',
  'cdots', // only in matrix
  'ddots', // only in matrix
  'vdots', // only in matrix

  // Arrows
  'to',
  'longleftarrow',
  'gets',
  'longrightarrow',
  'leftrightarrow',
  'longleftrightarrow',
  'mapsto',
  'longmapsto',

  // Geometric
  'angle',
  'triangle',
  'bigodot', // Used to indicate circles.
  'cong',
  'ncong',
  'equiv',
  // Negation of \equiv doesn't have its own command. Its done by \not \equiv.
  // But because \not can be generically composed I am not going to enable it.
  // Doing so would mean people start using the wrong latex for stuff.
  // eg "\not=" instead of "\neq", "\not \cong" instead of "\ncong" etc.
  'sim',
  'parallel',
  'perp',
  'overline',
  'overleftarrow',
  'overrightarrow',
  'overleftrightarrow',
  'overset',
  'large', // 🦋 This opens us up for abuse
  'frown',
  'sin',
  'cos',
  'tan',
  'sec',
  'csc',
  'cosec',
  'cot',
  'arcsin',
  'arccos',
  'arctan',

  // Don't have a good category for these
  'pmod',
  'binom',
  'sum',
  'prod',
  'circ', // function composition. 🦋 This opens us up for abuse

  // Calculus
  'int',
  'lim',
  'rq',
  'dot',
  'ddot',
  'vert', // 🦋 This opens us up for abuse
  'partial',

  // Vectors
  'utilde',
  'mathbf', // 🦋 This opens us up for abuse
  'hat',
  'bullet',
  'langle',
  'rangle',
  // primary use for two following commands is for representing unit vectors
  // \hat{\imath} => î, \hat{\jmath} => ĵ
  'imath', // dotless i
  'jmath', // dotless j

  // Matrices
  'begin', // 🦋 This opens us up for abuse
  'end', // 🦋 This opens us up for abuse
  'det',
  'hdashline',
  'hline',

  // Modifiers
  'cancel', // Strike through to show cancellation of values/terms etc.
]);

// Similar to typeof keyword except:
// 1. arrays have type 'array' instead of 'object'
// 2. null has type 'null' instead of 'object'
function typeOf(value) {
  if (Array.isArray(value)) return 'array';
  if (value === null) return 'null';
  return typeof value;
}

function isObject(maybeObj): boolean %checks {
  return typeof maybeObj === 'object' && maybeObj !== null;
}

function isNode(propValue) {
  switch (typeOf(propValue)) {
    case 'number':
    case 'string':
    case 'undefined':
    case 'null':
      return true;
    case 'boolean':
      return propValue === false; // true is not a valid node.
    case 'array':
      return propValue.every(isNode);
    case 'object':
      if (ReactIs.isElement(propValue)) return true;
      if (!propValue[Symbol.iterator]) return false;
      return [...propValue].every(isNode);
    default:
      return false;
  }
}

// It's actually a fair bit more complicated to do this robustly, but
// this will work for our purposes for now.
// See: https://github.com/facebook/react/blob/master/packages/shared/getComponentName.js
function getComponentName(type) {
  // When we wrap components — eg withValidation(Wrapped) — we add this
  // special __wrappedComponent property to the wrapping component so
  // that we do spec checking against the underlying wrapped component.
  // The wrappers are used to enhance the components only in the editor
  // environment, so we want to pretend they don't exist for the purposes
  // of spec checking.
  const component =
    type.__wrappedComponent !== undefined ? type.__wrappedComponent : type;

  if (typeof component === 'string') {
    // Intrinsic react-dom types (eg. 'div')
    return component;
  }

  return component.displayName || component.name || null;
}

type Spec = { [propName: string]: Checker };
type Props = { [propName: string]: mixed };
type Location = {|
  startLineNumber: number,
  startColumn: number,
  endLineNumber: number,
  endColumn: number,
|}; // TODO better type
type LocationData = { [propName: string]: Location };

// The document compiler that runs in the editor adds certain
// props in order to instrument / provide extra capabilities
// to the components in the live preview. We do not want these
// props to be part of the public interface, so we don't want
// them in the spec. But we don't want to see failures due
// to these un-spec'ed existing at runtime.
//
// PONDER: This does open up a way for people to write unwanted
// code in the editor though. Ie. there can be spurious _blah
// props that won't get called out as being problematic. Users
// could also write _sourceLocations or some other symbol we want
// to provide, which will break the runtime in weird ways.
function isDocumentCompilerInjectedProp(propName) {
  if (propName.startsWith('_')) return true;

  // To provide the ability for elements to easily lookup metadata about
  // themselves, we assign each element in the content-authored jsx
  // a unique id via a special $$elementId prop
  if (propName === '$$elementId') return true;

  return false;
}

// TODO this is shockingly brittle. Figure out a way to determine this robustly.
// The difficulty comes in that optional checkers are dynamically created.
function isOptionalSpec(spec) {
  return spec.name === 'optionalChecker';
}

function checkPropTypes(
  spec: Spec,
  props: Props,
  componentName: string,
  locations: LocationData,
): [FailureMessage[], { propName: string, failure: * }[]] {
  const rootFailures = [];
  for (const [propName, checker] of Object.entries(spec)) {
    // Don't perform checks against missing props. Let that be handled by the
    // missing props check instead. Otherwise we get useless failures about
    // undefined not being a valid value for a prop.
    if (!props.hasOwnProperty(propName)) continue;
    const failure = checker(props, propName);
    if (failure !== null) {
      rootFailures.push({ propName, failure });
    }
  }

  // TODO how do we add our location data?
  const failureMessages = rootFailures.map(({ propName, failure }) => ({
    // Should often come out as:
    // Latex: `expression` needs to be passed a string, not a number.
    message: `${componentName}'s prop \`${propName}\` ${failure.reason}`,
    ...locations[propName],
  }));

  // We will enforce that specs define the exact object shape of the props.
  const excessProps = [];
  for (const propName of Object.keys(props)) {
    if (
      !spec.hasOwnProperty(propName) &&
      !isDocumentCompilerInjectedProp(propName)
    ) {
      excessProps.push(propName);
    }
  }
  if (excessProps.length > 0) {
    failureMessages.push(
      ...excessProps.map((propName) => ({
        message: `${componentName} must NOT be given a \`${propName}\` prop`,
        ...locations[componentName],
      })),
    );
  }
  const missingProps = [];
  for (const propName of Object.keys(spec)) {
    if (!props.hasOwnProperty(propName) && !isOptionalSpec(spec[propName])) {
      missingProps.push(propName);
    }
  }
  if (missingProps.length > 0) {
    failureMessages.push(
      ...missingProps.map((propName) => ({
        message: `${componentName} must be given a \`${propName}\` prop`,
        ...locations[componentName],
      })),
    );
  }

  return [failureMessages, rootFailures];
}

const types: {|
  array: Checker,
  boolean: Checker,
  function: Checker,
  number: Checker,
  object: Checker,
  string: Checker,
  symbol: Checker,
  any: Checker,
  arrayOf: typeof createArrayOfChecker,
  instanceOf: typeof createInstanceOfChecker,
  objectOf: typeof createObjectOfChecker,
  // TODO is unionOfValues vs unionOfTypes really necessary?
  // Eg. in flow its fine to go. type X = string | 2 | 3;
  unionOfValues: typeof createUnionOfValuesChecker,
  unionOfTypes: typeof createUnionOfTypesChecker,
  shape: typeof createShapeChecker,
  exact: typeof createExactShapeChecker,
  tuple: typeof createTupleChecker,
  node: Checker,
  element: Checker,
  component: Checker,
  elementType: typeof createElementTypeChecker,
  childrenArray: typeof createChildrenArrayChecker,
  minimumCount: typeof createMinimumCountChecker,
  exactOrderChildrenArray: typeof createExactOrderChildrenArrayChecker,
  prefixChildrenArray: typeof createPrefixChildrenArrayChecker,
  supersetChecker: typeof createSupersetChecker,
  children: typeof createChildrenChecker,
  optional: typeof createOptionalChecker,
  uuid: Checker,
  latex: Checker,
|} = {
  // Primitives
  array: createPrimitiveTypeChecker('array'),
  boolean: createPrimitiveTypeChecker('boolean'),
  function: createPrimitiveTypeChecker('function'),
  number: createPrimitiveTypeChecker('number'),
  object: createPrimitiveTypeChecker('object'),
  string: createPrimitiveTypeChecker('string'),
  symbol: createPrimitiveTypeChecker('symbol'),

  // Generic JavaScript ones
  any: anyChecker,
  arrayOf: createArrayOfChecker,
  instanceOf: createInstanceOfChecker,
  objectOf: createObjectOfChecker,
  unionOfValues: createUnionOfValuesChecker,
  unionOfTypes: createUnionOfTypesChecker,
  shape: createShapeChecker,
  exact: createExactShapeChecker,
  tuple: createTupleChecker,

  // React-specific ones
  node: nodeChecker,
  element: elementChecker, // checks if its an element. eg <div />
  component: componentChecker, // checks if its a component eg List
  elementType: createElementTypeChecker, // checks it its a particular element, eg <List />
  minimumCount: createMinimumCountChecker,
  exactOrderChildrenArray: createExactOrderChildrenArrayChecker,
  prefixChildrenArray: createPrefixChildrenArrayChecker,
  supersetChecker: createSupersetChecker,
  children: createChildrenChecker,
  childrenArray: createChildrenArrayChecker,

  // Used to modify a checker to be optional.
  // I've opted for this instead of the .isRequired fluent interface in
  // prop-types, as that approach makes for a clumsy implementation.
  optional: createOptionalChecker,

  // Domain-specific ones
  uuid: uuidChecker,
  latex: latexChecker,
};

export { types, checkPropTypes };
